import { Currency } from '@sequencehq/api/utils/commonEnums'
import { useQuery } from '@sequencehq/api/utils'
import {
  dashboardv99990101Client,
  Dashboardv99990101Api
} from '@sequencehq/api/dashboard/v99990101'
import { useAvailableCurrencies } from './useAvailableCurrencies'

type RevenueRecognitionOverview =
  Dashboardv99990101Api.GetRevenueRecognitionOverview.RevenueRecognitionOverviewResponse

export type RevRecOverviewSummary = RevenueRecognitionOverview['summary']
export type RevRecOverviewMonthlyDataPoint =
  RevenueRecognitionOverview['monthlyRevenue']
export type RevRecOverviewMonthlyRevenue =
  RevenueRecognitionOverview['monthlyRevenue']
export type RevRecOverviewCustomer =
  RevenueRecognitionOverview['topCustomers']['customers'][number]
export type RevRecOverviewConcentrationRisk =
  RevenueRecognitionOverview['topCustomers']['concentrationRisk']
export type RevRecOverviewTopCustomers =
  RevenueRecognitionOverview['topCustomers']
export type RevRecOverviewData = RevenueRecognitionOverview

export interface RevRecOverviewFilters {
  currency?: Currency | null
  startDate: string
  endDate: string
}

export interface RevRecOverviewResponse {
  isLoading: boolean
  overview: {
    data?: RevRecOverviewData | null
    isLoading: boolean
    error?: string
  }
}

export const useRevRecOverviewData = (
  filters: RevRecOverviewFilters
): RevRecOverviewResponse => {
  const { currency, startDate, endDate } = filters
  const { availableCurrencies, isLoading } = useAvailableCurrencies()
  const preferredDefault =
    !isLoading && availableCurrencies
      ? availableCurrencies.includes('USD' as Currency)
        ? ('USD' as Currency)
        : (availableCurrencies[0] as Currency)
      : undefined
  const effectiveCurrency = (currency ?? preferredDefault) as
    | Currency
    | undefined

  const queryOptions = {
    enabled: !!effectiveCurrency,
    staleTime: 60000
  }

  const overviewResponse = useQuery(
    dashboardv99990101Client.getRevenueRecognitionOverview,
    {
      currency: (effectiveCurrency as Currency) ?? 'USD',
      startDate,
      endDate
    },
    queryOptions
  )

  return {
    isLoading: overviewResponse.isLoading,
    overview: {
      data: overviewResponse.data,
      isLoading: overviewResponse.isLoading,
      error: overviewResponse.error?.statusText
    }
  }
}
